import { Sine, TimelineMax, TweenLite, TweenMax } from "gsap";
import * as HLF from "html-living-framework";
import * as Constants from "../Data/Constants";
import * as Topics from "../Data/Topics";
import { TintFilter } from "../Filters/TintFilter";

export class HealthBar extends PIXI.Container {
    private _bg: PIXI.Sprite;
    private _fill: PIXI.Sprite;
    private _fillBg: PIXI.Sprite;
    private _fillMask: PIXI.Graphics;
    private _icon: PIXI.Sprite;

    private _health: number;
    private _tint: HLF.Colour;
    private _tintPerc: number;

    private _tintFilter: TintFilter;

    private _fillTween: TweenLite | undefined;
    private _tintTween: TweenMax | undefined;
    private _pulseTween: TimelineMax | undefined;
    private _heartTween: TimelineMax | undefined;
    private _subscriptions: any[];

    constructor(pBrokenHeart: boolean = false) {
        super();
        this._health = 1;
        this._tint = new HLF.Colour(255, 100, 100);
        this._tintPerc = 0;

        this._bg = HLF.PixiUtils.makeSprite("healthbar-bg", Constants.SPRITESHEET_UI);
        this.addChild(this._bg);

        this._fillBg = HLF.PixiUtils.makeSprite("healthbar-empty", Constants.SPRITESHEET_UI);
        this._fillBg.position.set(8, 0);
        this.addChild(this._fillBg);

        this._fill = HLF.PixiUtils.makeSprite("healthbar-fill", Constants.SPRITESHEET_UI);
        this._fill.position.copy(this._fillBg.position);
        this.addChild(this._fill);

        const asset: string = "healthbar-icon" + (pBrokenHeart ? "-broken" : "");
        this._icon = HLF.PixiUtils.makeSprite(asset, Constants.SPRITESHEET_UI);
        this._icon.position.set(-this._fill.width * 0.5 + 5, 3);
        this.addChild(this._icon);

        this._fillMask = new PIXI.Graphics();
        this._fillMask.beginFill(0x000000);
        this._fillMask.drawPolygon([
            new PIXI.Point(-this._fill.width * 0.5, -this._fill.height * 0.5),
            new PIXI.Point(this._fill.width * 0.5 - 5, -this._fill.height * 0.5),
            new PIXI.Point(this._fill.width * 0.5, this._fill.height * 0.5),
            new PIXI.Point(-this._fill.width * 0.5, this._fill.height * 0.5),
        ]);
        this._fillMask.endFill();
        this._fillMask.position.copy(this._fill.position);
        this.addChild(this._fillMask);
        this._fill.mask = this._fillMask;

        this._tintFilter = new TintFilter(HLF.Colour.WHITE.toWebGL());

        this._subscriptions = new Array<any>();
        this._subscriptions.push(PubSub.subscribe(Topics.UPDATE_HEALTH, this.updateHealth.bind(this)));
        this._subscriptions.push(PubSub.subscribe(Topics.FLASH_HEALTH_BAR, this.flashRed.bind(this)));
    }

    private get tintPerc(): number {
        return this._tintPerc;
    }

    private set tintPerc(pValue: number) {
        this._tintPerc = pValue;
        const tint: number[] = HLF.Colour.lerp(HLF.Colour.WHITE, this._tint, this._tintPerc).toWebGL();
        tint.push(1);
        this._tintFilter.uniforms.uTint = tint;
    }

    public destroy(pOptions?: boolean | PIXI.DestroyOptions): void {
        super.destroy(pOptions);
        for (let i = 0; i < this._subscriptions.length; ++i) {
            PubSub.unsubscribe(this._subscriptions[i]);
        }
        if (this._fillTween !== undefined) { this._fillTween.kill(); }
        if (this._tintTween !== undefined) { this._tintTween.kill(); }
        if (this._pulseTween !== undefined) { this._pulseTween.kill(); }
        if (this._heartTween !== undefined) { this._heartTween.kill(); }
    }

    public setHealth(pHealth: number, pInstantly: boolean = false): void {
        this._health = pHealth;

        if (this._fillTween !== undefined) {
            this._fillTween.kill();
        }

        this._fillTween = TweenLite.to(this._fillMask, pInstantly ? 0 : 0.5, {
            x: this._fillBg.position.x - this._fill.width * (1 - this._health),
            ease: Sine.easeInOut,
        });
    }

    public playPulse(): void {
        if (this._pulseTween !== undefined) { this._pulseTween.kill(); }

        this._pulseTween = new TimelineMax({
            repeat: -1,
        });

        // const cachedScale: number = this.scale.x;
        // this.scale.set(1);
        this._pulseTween.to(this.scale, 0.75, {
            x: 1.05,
            y: 1.05,
            ease: Sine.easeInOut,
        });
        this._pulseTween.to(this.scale, 0.75, {
            x: 1,
            y: 1,
            ease: Sine.easeInOut,
        });
        // this.scale.set(cachedScale);
    }

    public stopPulse(): void {
        if (this._pulseTween !== undefined) { this._pulseTween.kill(); }
    }

    public swapHeart(pBroken: boolean): void {
        if (this._heartTween !== undefined) { this._heartTween.kill(); }

        this._heartTween = new TimelineMax();
        this._heartTween.to(this._icon, 0.3, {
            alpha: 0,
            ease: Sine.easeInOut,
        });
        this._heartTween.call(() => {
            const asset: string = "healthbar-icon" + (pBroken ? "-broken" : "");
            this._icon.texture = PIXI.loader.resources[Constants.SPRITESHEET_UI].textures![asset];
        }, undefined, this);
        this._heartTween.to(this._icon, 0.3, {
            alpha: 1,
            ease: Sine.easeInOut,
        });
    }

    private updateHealth(pTopic: string, pValue: number): void {
        this.setHealth(pValue);
    }

    private flashRed(): void {
        this._filters = [ this._tintFilter ];
        this._tintPerc = 0;
        this._tintTween = TweenMax.to(this, 0.33, {
            tintPerc: 1,
            ease: Sine.easeInOut,
            repeat: 3,
            yoyo: true,
            onComplete: () => { this._filters = []; },
        });
    }
}
